import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, openProposalPdf } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Download, ChevronLeft, FileSignature, Send, CheckCircle2, XCircle,
  History, Copy, AlertTriangle, Lock, RotateCcw, RefreshCw,
} from 'lucide-react';
import ProposalConditionsEditor from '@/components/ProposalConditionsEditor';

function fmtINR(n) {
  if (n == null) return '—';
  try { return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`; }
  catch { return `₹${n}`; }
}

export default function ProposalDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPerm } = useAuth();
  const [p, setP] = useState(null);
  const [pdfCheck, setPdfCheck] = useState(null);
  const [fixOpen, setFixOpen] = useState(false);
  const [fix, setFix] = useState({ client_name: '', validity_date: '', prepared_by_name: '', prepared_by_phone: '' });
  const [savingFix, setSavingFix] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendData, setSendData] = useState({ to: '', cc: '', share_in_client_portal: false, fallback: null });
  const [sendConfig, setSendConfig] = useState({ resend_enabled: false, sender_email: '' });
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [workflow, setWorkflow] = useState({ require_approval: false });
  const [showChanges, setShowChanges] = useState(false);

  const load = () => api.get(`/proposals/${id}`).then((r) => setP(r.data)).catch(() => setP(null));
  const loadPdfCheck = () => api.get(`/proposals/${id}/pdf-check`)
    .then((r) => setPdfCheck(r.data)).catch(() => setPdfCheck(null));
  useEffect(() => {
    load();
    loadPdfCheck();
    api.get('/proposals/send-config').then((r) => setSendConfig(r.data)).catch(() => {});
    api.get('/settings/proposals-workflow')
      .then((r) => setWorkflow(r.data || { require_approval: false }))
      .catch(() => {});
  }, [id]);

  const openFix = () => {
    setFix({
      client_name:        p.client_name        || '',
      validity_date:      p.validity_date      || '',
      prepared_by_name:   p.prepared_by_name   || '',
      prepared_by_phone:  p.prepared_by_phone  || '',
    });
    setFixOpen(true);
  };

  const saveAndDownload = async () => {
    setSavingFix(true);
    try {
      // Only send fields that actually changed to keep history clean.
      const patch = {};
      for (const k of ['client_name', 'validity_date', 'prepared_by_name', 'prepared_by_phone']) {
        if ((fix[k] || '') !== (p[k] || '')) patch[k] = fix[k];
      }
      if (Object.keys(patch).length) {
        await api.patch(`/proposals/${id}`, patch);
      }
      // Re-run the dry check to confirm nothing else is blocking.
      const chk = (await api.get(`/proposals/${id}/pdf-check`)).data;
      setPdfCheck(chk);
      await load();
      if (!chk.ok) {
        toast.error(`Still blocking: ${chk.issues.map((i) => i.label).join(', ')}`);
        setSavingFix(false);
        return;
      }
      setFixOpen(false);
      setSavingFix(false);
      // Open the PDF immediately.
      await openProposalPdf(id, {
        mode: 'open',
        filename: `${p.number || 'proposal'}.pdf`,
        onError: (m, status) => toast.error(status ? `${m} (HTTP ${status})` : m),
      });
    } catch (e) {
      setSavingFix(false);
      toast.error(e?.response?.data?.detail || e.message || 'Could not save');
    }
  };

  if (!p) return <div className="max-w-3xl mx-auto px-4 py-8 text-ink-2">Loading…</div>;

  const canApprove = hasPerm('proposals_approve');
  const canSend = hasPerm('proposals_create');
  const moneyHidden = !!p.money_hidden;
  const canSeeMoney = !moneyHidden;
  const isDraft = p.status === 'Draft';
  const isPendingApproval = p.status === 'Pending approval';
  const isGenerated = p.status === 'Generated';
  const isOwner = (user?.role === 'Owner');
  const approvalEnabled = !!workflow.require_approval;
  const canCreateRevision = canSend && !['Cancelled', 'Superseded'].includes(p.status);
  const isTerminal = ['Cancelled', 'Superseded'].includes(p.status);
  const versionNum = p.version_counter || 1;

  const doAction = async (fn, label) => {
    try { await fn(); toast.success(label); load(); loadPdfCheck(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const openSend = () => {
    // Prefill `to` from the proposal's client contact (or lead contact if
    // the proposal is for a lead). The user can still edit the field.
    const prefillTo = (p.contact_email || p.client_contact_email || p.lead_contact_email || '').trim();
    setSendData({
      to: prefillTo,
      to_name: p.contact_name || p.client_contact_name || p.lead_contact_name || '',
      cc: p.prepared_by_email || '',
      share_in_client_portal: false,
      fallback: null,
      missing_email: !prefillTo,          // UI hint when we have nothing to prefill
    });
    setSendOpen(true);
  };

  const doSend = async () => {
    try {
      const to = sendData.to.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
      const cc = sendData.cc.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
      if (to.length === 0) return toast.error('Add at least one recipient');
      const { data } = await api.post(`/proposals/${p.id}/send`, {
        to, cc, share_in_client_portal: sendData.share_in_client_portal,
      });
      if (data.ok) {
        if (data.fallback_used) {
          toast.warning('Sent via fallback sender', {
            description: data.warning || `Email was sent from ${data.fallback_sender} because the configured sender's domain is not verified in Resend.`,
            duration: 9000,
          });
        } else {
          toast.success('Sent via Resend');
        }
        setSendOpen(false); load();
      } else if (data.fallback) {
        setSendData({ ...sendData, fallback: data });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Send failed'); }
  };

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="proposal-detail-page">
      <div className="flex items-center justify-between gap-3">
        <Link to="/proposals" className="text-xs text-ink-2 inline-flex items-center gap-1 hover:text-ink">
          <ChevronLeft className="h-3 w-3" /> All proposals
        </Link>
        <div className="flex gap-2">
          <Button variant="outline"
                  disabled={moneyHidden || (pdfCheck && !pdfCheck.ok)}
                  title={moneyHidden
                    ? 'PDF contains amounts. Ask Owner/Admin to enable see_money.'
                    : (pdfCheck && !pdfCheck.ok
                        ? `Fix: ${pdfCheck.issues.map((i) => i.label).join(', ')}`
                        : undefined)}
                  onClick={() => openProposalPdf(p.id, {
                    mode: 'open',
                    filename: `${p.number || 'proposal'}.pdf`,
                    onError: (m, status) => toast.error(status ? `${m} (HTTP ${status})` : m),
                  })}
                  data-testid="pdp-pdf-btn">
            <Download className="h-4 w-4 mr-1" /> PDF
          </Button>
          {/* "Send for approval" — only visible when approval is enabled globally. */}
          {approvalEnabled && isDraft && !p.approval_status && p.approval_mode !== 'owner_prepared' && (
            <Button variant="outline"
                    onClick={() => doAction(() => api.post(`/proposals/${p.id}/request-approval`), 'Sent for approval')}
                    data-testid="pdp-req-approve">
              <Send className="h-4 w-4 mr-1" /> Send for approval
            </Button>
          )}
          {approvalEnabled && isPendingApproval && canApprove && (
            <>
              <Button variant="outline"
                      onClick={() => setRejectOpen(true)}
                      data-testid="pdp-reject">
                <XCircle className="h-4 w-4 mr-1" /> Reject
              </Button>
              <Button onClick={() => doAction(() => api.post(`/proposals/${p.id}/approve`, {}), 'Approved')}
                      data-testid="pdp-approve">
                <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
              </Button>
            </>
          )}
          {/* Rejected: let Sales re-submit after editing */}
          {approvalEnabled && isDraft && p.approval_status === 'Rejected' && (
            <Button variant="outline"
                    onClick={() => doAction(() => api.post(`/proposals/${p.id}/request-approval`), 'Sent for approval')}
                    data-testid="pdp-req-approve-again">
              <Send className="h-4 w-4 mr-1" /> Send for approval again
            </Button>
          )}
          {/* ─── EDIT ─── Open-editing: single button in every non-terminal
                status, opens the full editor. No "reopen" step, no status
                change. */}
          {!isTerminal && canSend && (
            <Button
              variant="outline"
              onClick={() => navigate(`/proposals/${p.id}/edit`)}
              data-testid="pdp-edit">
              <RotateCcw className="h-4 w-4 mr-1" /> Edit
            </Button>
          )}
          {/* Generate — only on Draft. When approval is OFF, always allowed. */}
          {isDraft && (!approvalEnabled
                       || p.approval_status === 'Approved'
                       || p.approval_mode === 'owner_prepared'
                       || !p.needs_approval) && (
            <Button
              onClick={() => doAction(() => api.post(`/proposals/${p.id}/generate`), 'Generated')}
              data-testid="pdp-generate">
              Generate
            </Button>
          )}
          {/* Send + Send-again for any status past Draft */}
          {!isTerminal && !isDraft && canSend && (
            <Button variant={p.needs_resend ? 'default' : 'outline'} onClick={openSend} data-testid={p.status === 'Generated' ? 'pdp-send' : 'pdp-resend'}>
              <Send className="h-4 w-4 mr-1" /> {p.status === 'Generated' ? 'Send' : 'Send again'}
            </Button>
          )}
          {/* Save-as-new-revision — optional, available anywhere except Cancelled/Superseded. */}
          {canCreateRevision && !isDraft && (
            <Button variant="outline"
                    onClick={() => {
                      if (!window.confirm('Save a copy of this proposal as a new revision? This one stays as it is and can still be edited.')) return;
                      api.post(`/proposals/${p.id}/revision`)
                        .then(({ data }) => { toast.success(`Revision ${data.number} created`); navigate(`/proposals/${data.id}`); })
                        .catch((e) => toast.error(e?.response?.data?.detail || 'Failed'));
                    }}
                    data-testid="pdp-revision">
              + Save as new revision
            </Button>
          )}
          {/* Owner-only: Delete (soft) / Restore. */}
          {isOwner && !p.deleted_at && !isTerminal && (
            <Button variant="outline"
                    onClick={() => {
                      if (!window.confirm('Soft-delete this proposal? It can be restored later.')) return;
                      doAction(() => api.delete(`/proposals/${p.id}`), 'Deleted');
                    }}
                    data-testid="pdp-delete"
                    className="text-rose-700 hover:text-rose-800">
              Delete
            </Button>
          )}
          {isOwner && p.deleted_at && (
            <Button variant="outline"
                    onClick={() => doAction(() => api.post(`/proposals/${p.id}/restore`), 'Restored')}
                    data-testid="pdp-restore">
              Restore
            </Button>
          )}
        </div>
      </div>

      <Card className="shine-card p-5 rounded-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-ink flex items-center gap-2">
              <FileSignature className="h-5 w-5 text-green-ink" /> {p.number}
              {p.revision > 1 && <Badge variant="outline">R{p.revision}</Badge>}
            </h1>
            <div className="text-sm text-ink-2 mt-1">{p.client_name}</div>
            {p.latest_revision_id && p.latest_revision_id !== p.id && (
              <button type="button" className="mt-1 text-xs text-indigo-700 hover:underline"
                      onClick={() => navigate(`/proposals/${p.latest_revision_id}`)}
                      data-testid="pdp-newer-revision">
                Newer revision: {p.latest_revision_number} →
              </button>
            )}
          </div>
          <div className="text-right">
            <Badge className="bg-slate-100 text-slate-700 border-0">{p.status}</Badge>
            {versionNum > 1 && (
              <Badge variant="outline" className="ml-1 text-[10px]" data-testid="pdp-version-chip">v{versionNum}</Badge>
            )}
            {p.edited_after_send && (
              <div className="text-xs text-amber-700 mt-1 inline-flex items-center gap-1" data-testid="pdp-edited-chip">
                <AlertTriangle className="h-3 w-3" />
                Edited after sending{p.last_edited_at ? ` · ${(p.last_edited_at || '').slice(0, 10)}` : ''}
              </div>
            )}
            {p.deleted_at && (
              <div className="text-xs text-rose-700 mt-1 inline-flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                Soft-deleted
              </div>
            )}
            {approvalEnabled && p.needs_approval && (
              <div className="text-xs text-amber-700 mt-1 inline-flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {p.approval_status === 'Approved'
                  ? 'Approved'
                  : (p.approval_mode === 'always' ? 'Approval required' : 'Needs approval')}
              </div>
            )}
            {approvalEnabled && p.approval_status === 'Approved' && (p.status === 'Draft') && (
              <div className="text-xs text-emerald-700 mt-1 inline-flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" />
                Approved
              </div>
            )}
            {approvalEnabled && p.approval_mode === 'owner_prepared' && (
              <div className="text-xs text-emerald-700 mt-1 inline-flex items-center gap-1" data-testid="pdp-owner-prepared">
                <CheckCircle2 className="h-3 w-3" />
                Owner-prepared · no approval needed
              </div>
            )}
          </div>
        </div>

        {/* Rejection reason — surfaced to the preparing salesperson */}
        {approvalEnabled && p.approval_status === 'Rejected' && isDraft && (
          <div className="mt-3 bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm" data-testid="pdp-rejection-banner">
            <div className="flex items-center gap-2 text-rose-800 font-semibold">
              <XCircle className="h-4 w-4" /> Rejected by {p.approval_decided_by_name || 'Owner'}
            </div>
            <div className="text-rose-700 text-xs mt-1">
              {p.approval_reason || 'No reason given.'} Fix the proposal and send it for approval again.
            </div>
          </div>
        )}
        {approvalEnabled && p.approval_status === 'Approved' && isDraft && (
          <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
            <div className="flex items-center gap-2 text-emerald-800 font-semibold">
              <CheckCircle2 className="h-4 w-4" /> Approved by {p.approval_decided_by_name || 'Owner'}
            </div>
            <div className="text-emerald-700 text-xs mt-1">
              You can now Generate and Send this proposal.
            </div>
          </div>
        )}
        {pdfCheck && !pdfCheck.ok && pdfCheck.can_download && (
          <div className="mt-3 bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm" data-testid="pdp-pdf-check-banner">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-amber-800 font-semibold">
                <AlertTriangle className="h-4 w-4" /> This proposal cannot be downloaded as PDF yet
              </div>
              {isDraft && (
                <Button size="sm" onClick={openFix} data-testid="pdp-fix-now">
                  Fix now &amp; download
                </Button>
              )}
            </div>
            <ul className="text-amber-800 text-xs mt-1.5 ml-5 list-disc space-y-0.5">
              {pdfCheck.issues.map((i) => (
                <li key={i.field}><b>{i.label}</b> — {i.hint}</li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      <Card className="shine-card p-5 rounded-xl space-y-3">
        <h2 className="text-sm font-semibold text-ink">Line items · {p.lines?.length || 0}</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-left text-ink-2 uppercase border-b border-ink/10">
              <tr><th className="py-2 pr-2">#</th><th className="py-2 pr-2">Service</th><th className="py-2 pr-2">Details</th><th className="py-2 pr-2 text-right">Amount</th></tr>
            </thead>
            <tbody>
              {(p.lines || []).map((ln, i) => (
                <tr key={i} className="border-b border-ink/5">
                  <td className="py-2 pr-2">{i + 1}</td>
                  <td className="py-2 pr-2">{ln.service_key}</td>
                  <td className="py-2 pr-2">
                    {ln.service_key === 'theatre_ads' && (
                      <>
                        {`${ln.attributes?.theatre || ln.attributes?.theatre_code} · ${ln.network} · ${(ln.rate_type || '').toUpperCase()} · ${ln.duration_seconds || 10}s · ${ln.screens}×${ln.weeks}w`}
                        {ln.attributes?.theatre_code != null && String(ln.attributes.theatre_code).length > 0 && (
                          <span className="ml-2 inline-flex items-center rounded border border-ink/15 bg-ink-7 px-1.5 py-0.5 text-[10px] font-mono text-ink-2" data-testid="line-web-code">
                            Web code: {String(ln.attributes.theatre_code)}
                          </span>
                        )}
                        {ln.rate != null && ` @ ₹${ln.rate}`}
                        {ln.start_warning && (
                          <div className="mt-1 inline-flex items-start gap-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                            <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                            <span>
                              Start date was not a Friday — pricing anchored on your exact start ({ln.weekly_breakup?.[0]?.week_from}).
                              {ln.suggested_next_friday && (
                                <> Next Friday on or after: <b>{ln.suggested_next_friday}</b>.</>
                              )}
                            </span>
                          </div>
                        )}
                        {ln.season_summary && (
                          <div className="text-[10px] text-ink-2 mt-0.5">Seasons: {ln.season_summary}</div>
                        )}
                      </>
                    )}
                    {ln.service_key === 'newspaper_eenadu' && `${ln.newspaper} · ${ln.edition} · ${ln.width_cm}×${ln.height_cm}cm · ${ln.colour} · ${ln.insertions} ins`}
                    {(ln.service_key || '').startsWith('product_catalog') && (
                      <div>
                        <div>{ln.item}</div>
                        <div className="text-[11px] text-ink-2">
                          {ln.quantity ?? 1} × {canSeeMoney ? fmtINR(ln.rate) : '—'}{Number(ln.duration || 1) !== 1 ? ` × ${ln.duration} ${ln.unit_type || ''}` : ''}
                        </div>
                        {ln.description && <div className="text-[11px] text-ink-2 whitespace-pre-wrap">{ln.description}</div>}
                      </div>
                    )}
                    {ln.service_key === 'custom' && (
                      <div>
                        <div>{ln.item || ln.description}</div>
                        {ln.item && ln.description && (
                          <div className="text-[11px] text-ink-2 whitespace-pre-wrap mt-0.5">{ln.description}</div>
                        )}
                      </div>
                    )}
                    {ln.service_key === 'messaging_campaign' && (
                      <div>
                        <div className="font-medium">{ln.channel_label || ln.channel_key}</div>
                        {ln.description && (
                          <div className="text-[11px] text-ink-2 whitespace-pre-wrap mt-0.5">{ln.description}</div>
                        )}
                        <div className="text-[11px] text-ink-2 mt-0.5">
                          {ln.sender_detail ? `${ln.sender_detail} · ` : ''}
                          {Number(ln.recipients || 0).toLocaleString('en-IN')} {ln.unit_label || 'message'}
                          {ln.channel_key === 'sms_promotional' && ln.sms_segments ? ` × ${ln.sms_segments} seg` : ''}
                          {ln.billing_event ? ` · ${ln.billing_event}` : ''}
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-2 text-right tabular-nums">{canSeeMoney ? fmtINR(ln.line_total ?? ln.amount) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Approver side-by-side for product_catalog: catalog rate vs
            proposal rate, and original description vs edited description.
            Only renders when a difference actually exists on at least one
            line so there's no noise for straight-through proposals. */}
        {p.proposal_type === 'product_catalog' && (p.lines || []).some(
          (ln) => (ln.original_rate != null && Number(ln.original_rate) !== Number(ln.rate))
                 || ((ln.description_edited || ln._edited_desc)
                     && ln.original_description
                     && ln.original_description !== ln.description)
        ) && (
          <div className="mt-3 border border-amber-200 bg-amber-50/60 rounded-lg p-3"
               data-testid="pdp-approver-diffs">
            <div className="text-xs font-semibold text-amber-800 uppercase mb-2">
              Changes vs the catalog
            </div>
            <div className="space-y-2 text-xs">
              {(p.lines || []).map((ln, i) => {
                const rateDiff = (ln.original_rate != null
                                  && Number(ln.original_rate) !== Number(ln.rate));
                const descDiff = (ln.description_edited || ln._edited_desc)
                                 && ln.original_description
                                 && ln.original_description !== ln.description;
                if (!rateDiff && !descDiff) return null;
                return (
                  <div key={i} className="border-l-2 border-amber-300 pl-2">
                    <div className="font-semibold text-ink">
                      Line {i + 1} · {ln.item || '—'}
                    </div>
                    {rateDiff && (
                      <div className="text-ink-2">
                        Rate — catalog <span className="line-through">{fmtINR(ln.original_rate)}</span>
                        {' → '}
                        <span className="font-semibold text-ink">{fmtINR(ln.rate)}</span>
                      </div>
                    )}
                    {descDiff && (
                      <div className="grid grid-cols-2 gap-2 mt-1">
                        <div className="text-ink-2">
                          <div className="text-[10px] uppercase text-ink-2/70">Catalog text</div>
                          <div className="whitespace-pre-wrap">{ln.original_description}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase text-amber-800">Edited for this proposal</div>
                          <div className="whitespace-pre-wrap text-ink">{ln.description}</div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Weekly schedule — one row per Fri→Thu week showing which season rate
            was applied. Only shown when at least one theatre line produced a
            weekly_breakup via the pricing engine. */}
        {(p.lines || []).some((l) => (l.weekly_breakup || []).length > 0) && (
          <div className="pt-3 border-t border-ink/10">
            <h3 className="text-xs font-semibold text-ink-2 uppercase mb-2">Schedule</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="weekly-schedule">
                <thead className="text-left text-ink-2 uppercase border-b border-ink/10">
                  <tr>
                    <th className="py-1.5 pr-2">Theatre</th>
                    <th className="py-1.5 pr-2">Dur.</th>
                    <th className="py-1.5 pr-2">Week</th>
                    <th className="py-1.5 pr-2">From</th>
                    <th className="py-1.5 pr-2">To</th>
                    <th className="py-1.5 pr-2">Season</th>
                    <th className="py-1.5 pr-2 text-right">Rate</th>
                    <th className="py-1.5 pr-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(p.lines || []).flatMap((ln, li) =>
                    (ln.weekly_breakup || []).map((wk, wi) => (
                      <tr key={`${li}-${wi}`} className="border-b border-ink/5">
                        <td className="py-1.5 pr-2 text-ink">
                          {ln.attributes?.theatre || ln.attributes?.theatre_code || ln.network || 'Theatre'}
                        </td>
                        <td className="py-1.5 pr-2 tabular-nums">{ln.duration_seconds || 10}s</td>
                        <td className="py-1.5 pr-2">W{wi + 1}</td>
                        <td className="py-1.5 pr-2 text-ink-2">{wk.week_from}</td>
                        <td className="py-1.5 pr-2 text-ink-2">{wk.week_to}</td>
                        <td className="py-1.5 pr-2">
                          <Badge
                            variant="outline"
                            className={`text-[10px] ${wk.season_key === 'festive' ? 'border-rose-300 text-rose-700' : 'border-emerald-300 text-emerald-700'}`}>
                            {wk.season_label || wk.season_key || '—'}
                          </Badge>
                        </td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">
                          {canSeeMoney ? fmtINR(wk.rate) : '—'}
                        </td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">
                          {canSeeMoney ? fmtINR(wk.amount) : '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>

      <Card className="shine-card p-5 rounded-xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
          <div className="space-y-1">
            {canSeeMoney ? (
              <>
                <div><b>Subtotal:</b> {fmtINR(p.subtotal)}</div>
                {p.slab_discount_pct > 0 && <div><b>Slab discount:</b> {p.slab_discount_pct}% ({p.slab_label})</div>}
                {p.discount_pct > 0 && <div><b>Manual discount:</b> {p.discount_pct}% = {fmtINR(p.discount_amount)}</div>}
                <div><b>GST @ {p.gst_pct}%:</b> {fmtINR(p.gst_amount)}</div>
                <div className="text-xl font-bold text-green-ink pt-1 border-t border-ink/10 mt-2">
                  <b>Total:</b> {fmtINR(p.total)}
                </div>
              </>
            ) : (
              <div className="inline-flex items-center gap-2 text-ink-2 bg-page/80 border border-ink/10 px-3 py-2 rounded-md">
                <Lock className="h-4 w-4" />
                Amounts are hidden by your access settings (<code>see_money</code> is off).
              </div>
            )}
          </div>
          <div className="text-xs text-ink-2 space-y-1">
            <div className="uppercase font-semibold">Prepared by</div>
            <div className="text-ink">{p.prepared_by_name}, {p.prepared_by_designation}</div>
            <div>{p.prepared_by_phone}</div>
            <div>{p.prepared_by_email}</div>
            {p.to_emails && p.to_emails.length > 0 && (
              <>
                <div className="uppercase font-semibold mt-2">Sent to</div>
                <div className="text-ink">{p.to_emails.join(', ')}</div>
              </>
            )}
          </div>
        </div>
      </Card>

      {/* ADD-ON: Editable per-proposal conditions. Editable for any
          proposal that is not Cancelled or Superseded (open-editing). */}
      <Card className="shine-card p-5 rounded-xl" data-testid="proposal-conditions-card">
        <h2 className="text-sm font-semibold text-ink mb-3 flex items-center gap-2">
          <FileSignature className="h-4 w-4" /> Terms & conditions for this proposal
        </h2>
        <ProposalConditionsEditor
          value={p.conditions_snapshot || []}
          disabled={isTerminal || !canSend}
          title=""
          onChange={async (next) => {
            try {
              const r = await api.patch(`/proposals/${id}`, { conditions_snapshot: next });
              setP(r.data);
              toast.success('Conditions updated');
            } catch (e) {
              toast.error(e?.response?.data?.detail || 'Could not update');
            }
          }}
        />
      </Card>

      {/* History */}
      {(p.history && p.history.length > 0) && (
        <Card className="shine-card p-5 rounded-xl">
          <h2 className="text-sm font-semibold text-ink mb-2 flex items-center gap-2">
            <History className="h-4 w-4" /> History
          </h2>
          <ul className="space-y-1 text-xs text-ink-2">
            {p.history.map((h, i) => (
              <li key={i} className="flex gap-2">
                <span className="font-mono">{new Date(h.at).toLocaleString()}</span>
                <span className="text-ink font-medium">{h.action}</span>
                {h.actor_name && <span>· by {h.actor_name}</span>}
                {h.reason && <span>· "{h.reason}"</span>}
                {h.note && <span>· "{h.note}"</span>}
                {h.details && h.action === 'Conditions edited' && (
                  <span>
                    · {h.details.added?.length || 0} added, {h.details.removed?.length || 0} removed,
                    {' '}{h.details.changed?.length || 0} edited
                    {h.details.reordered ? ', reordered' : ''}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Changes (per-field diffs) */}
      {(p.change_log && p.change_log.length > 0) && (
        <Card className="shine-card p-5 rounded-xl" data-testid="pdp-changes-card">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-ink flex items-center gap-2">
              <RefreshCw className="h-4 w-4" /> Changes
              <span className="text-xs text-ink-2 font-normal">({p.change_log.length})</span>
            </h2>
            <Button size="sm" variant="ghost" onClick={() => setShowChanges((v) => !v)}>
              {showChanges ? 'Hide' : 'Show'}
            </Button>
          </div>
          {showChanges && (
            <div className="max-h-96 overflow-y-auto space-y-1 text-xs">
              {[...p.change_log].reverse().map((e, i) => (
                <div key={i} className="flex items-start gap-2 py-1 border-b border-ink/5">
                  <span className="font-mono text-ink-2 whitespace-nowrap">{(e.at || '').slice(0, 16).replace('T', ' ')}</span>
                  <span className="text-ink font-medium whitespace-nowrap">{e.label || e.field}</span>
                  <span className="text-ink-2">: <span className="line-through">{e.old === null || e.old === undefined ? '—' : String(e.old)}</span> → <b className="text-ink">{e.new === null || e.new === undefined ? '—' : String(e.new)}</b></span>
                  {e.actor_name && <span className="text-ink-2 ml-auto whitespace-nowrap">by {e.actor_name}</span>}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Send dialog */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="sm:max-w-xl" data-testid="send-dialog">
          <DialogHeader><DialogTitle>Send {p.number}</DialogTitle></DialogHeader>
          {!sendData.fallback ? (
            <div className="space-y-3 text-sm">
              {!sendConfig.resend_enabled && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                  <b>Email sending is not switched on yet.</b> Add <code>RESEND_API_KEY</code> to the backend .env and verify your sender domain. For now, "Send" will show a fallback with the email body + PDF download so you can send manually.
                </div>
              )}
              {sendConfig.resend_enabled && sendConfig.will_auto_fallback && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800" data-testid="send-fallback-warning">
                  <b>Heads-up:</b> the configured sender <code>{sendConfig.sender_email}</code> is on domain
                  {' '}<b>{sendConfig.sender_domain}</b>, which is <b>not verified</b> on Resend.
                  This email will be auto-sent from the verified fallback
                  {' '}<code>{sendConfig.fallback_sender_email}</code> instead. Update the sender in
                  {' '}<i>Proposal setup</i>, or verify the domain on Resend.
                </div>
              )}
              <div>
                <Label className="text-xs">From</Label>
                <Input
                  value={sendConfig.sender_display || sendConfig.sender_email || '(not configured)'}
                  disabled
                  data-testid="send-from"
                />
                {!sendConfig.resend_enabled && (
                  <p className="text-[11px] text-amber-700 mt-1">Resend is not switched on. Click Send to see the fallback body + PDF.</p>
                )}
              </div>
              <div>
                <Label className="text-xs">To (comma-separated)</Label>
                <Input
                  value={sendData.to}
                  onChange={(e) => setSendData({ ...sendData, to: e.target.value, missing_email: !e.target.value.trim() })}
                  placeholder={sendData.missing_email ? 'Add the client\'s email' : ''}
                  className={sendData.missing_email ? 'border-rose-300 focus-visible:ring-rose-400' : ''}
                  data-testid="send-to"
                />
                {sendData.missing_email && (
                  <p className="text-[11px] text-rose-700 mt-1" data-testid="send-missing-email">
                    No contact email on file for this {p.lead_id ? 'lead' : 'client'}. Add one above (or on the {p.lead_id ? 'lead' : 'client'} record) to send.
                  </p>
                )}
                {sendData.to_name && !sendData.missing_email && (
                  <p className="text-[11px] text-ink-2 mt-1">Prefilled from {sendData.to_name}'s contact.</p>
                )}
              </div>
              <div>
                <Label className="text-xs">CC (optional; salesperson will be auto-CC'd)</Label>
                <Input value={sendData.cc} onChange={(e) => setSendData({ ...sendData, cc: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Reply-To (locked — salesperson who prepared this proposal)</Label>
                <Input value={p.prepared_by_email || ''} disabled />
              </div>
              <div className="text-xs text-ink-2" data-testid="send-portal-note">
                The client can also see this proposal in their portal and Accept or Request changes.
              </div>
            </div>
          ) : (
            <div className="space-y-3 text-sm">
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs">
                <b>Fallback mode:</b> {sendData.fallback.message}
              </div>
              <div className="rounded-lg border border-ink/10 p-3 bg-page text-xs">
                <div><b>Subject:</b> {sendData.fallback.subject}</div>
                <div className="mt-2 whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: sendData.fallback.html }} />
              </div>
              <Button variant="outline" onClick={() => { navigator.clipboard.writeText(sendData.fallback.html); toast.success('Copied'); }}>
                <Copy className="h-3 w-3 mr-1" /> Copy email body
              </Button>
              <Button variant="outline"
                      onClick={() => openProposalPdf(p.id, {
                        mode: 'download',
                        filename: `${p.number || 'proposal'}.pdf`,
                        onError: (m) => toast.error(m),
                      })}>
                <Download className="h-3 w-3 mr-1" /> Download PDF
              </Button>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSendOpen(false)}>Close</Button>
            {!sendData.fallback && (
              <Button
                onClick={doSend}
                disabled={!(sendData.to || '').trim()}
                data-testid="send-confirm"
                title={!(sendData.to || '').trim() ? "Add the client's email first" : undefined}
              >
                <Send className="h-4 w-4 mr-1" /> {sendConfig.resend_enabled ? 'Send email' : 'Open fallback'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject-with-reason dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="max-w-md" data-testid="pdp-reject-dialog">
          <DialogHeader>
            <DialogTitle>Why are you rejecting this proposal?</DialogTitle>
          </DialogHeader>
          <Textarea rows={4} value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="e.g. Discount is too high for a first-time client — ask Sales to re-pitch at 10 %."
                    data-testid="pdp-reject-reason" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button
              onClick={async () => {
                if (!rejectReason.trim()) return toast.error('Please add a short reason');
                await doAction(() => api.post(`/proposals/${p.id}/reject`, { reason: rejectReason.trim() }),
                               'Rejected');
                setRejectOpen(false); setRejectReason('');
              }}
              data-testid="pdp-reject-submit">
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fix-now dialog — tiny inline-edit of the four fields that commonly
           block PDF generation on a Draft, then auto-retries the download. */}
      <Dialog open={fixOpen} onOpenChange={setFixOpen}>
        <DialogContent className="max-w-md" data-testid="pdp-fix-dialog">
          <DialogHeader>
            <DialogTitle>Fix missing fields & download</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label className="text-xs">Client <span className="text-rose-500">*</span></Label>
              <Input value={fix.client_name}
                     placeholder="e.g. VEM RESORTS"
                     onChange={(e) => setFix({ ...fix, client_name: e.target.value })}
                     data-testid="pdp-fix-client" />
            </div>
            <div>
              <Label className="text-xs">Valid till <span className="text-rose-500">*</span></Label>
              <Input type="date" value={fix.validity_date}
                     onChange={(e) => setFix({ ...fix, validity_date: e.target.value })}
                     data-testid="pdp-fix-valid-till" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Prepared-by name <span className="text-rose-500">*</span></Label>
                <Input value={fix.prepared_by_name}
                       onChange={(e) => setFix({ ...fix, prepared_by_name: e.target.value })}
                       data-testid="pdp-fix-prep-name" />
              </div>
              <div>
                <Label className="text-xs">Prepared-by phone <span className="text-rose-500">*</span></Label>
                <Input value={fix.prepared_by_phone}
                       onChange={(e) => setFix({ ...fix, prepared_by_phone: e.target.value })}
                       data-testid="pdp-fix-prep-phone" />
              </div>
            </div>
            <p className="text-xs text-ink-2">
              Saving will update the proposal and open the PDF in a new tab. Only Draft proposals can be edited here.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFixOpen(false)}>Cancel</Button>
            <Button onClick={saveAndDownload} disabled={savingFix} data-testid="pdp-fix-save">
              {savingFix ? 'Saving…' : 'Save & download'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
